import moment from "moment";
import React, { useState } from "react";
import {
  Button,
  Modal,
  Form,
  Container,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import {
  FaPlusCircle,
  FaHourglassHalf,
  FaCheckCircle,
  FaPauseCircle,
  FaTimesCircle,
  FaComment,
  FaEye,
  FaRegFileAlt,
  FaPaperclip,
  FaRegTimesCircle,
  FaBriefcase,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { createUserTickets } from "../../../redux/actions/TicketActions";
import Loader from "../../../components/loader/Loader";
import TicketDetailModal from "../../../components/modals/TicketDetailModal";
import { uploadTicketDocuments } from "../../../redux/actions/DocumentActions";

function UserPlatformGestTicket() {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const { user } = useSelector((state) => state.authUser);
  const { activeCompany } = useSelector((state) => state.companies);
  const [showModal, setShowModal] = useState(false);
  const { ticketTypes, userTickets } = useSelector((state) => state.tickets);
  const [formData, setFormData] = useState({ type: "", message: "" });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAttachment(file);
  };

  const openCommentModal = (ticket) => {
    let type = getNameByCode(ticket.reclamationCode);
    setSelectedTicket({
      ...ticket,
      type,
    });
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setSelectedTicket(null);
    setShowCommentModal(false);
  };

  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);

  const openDetailModal = (ticket) => {
    let type = getNameByCode(ticket.reclamationCode);
    setSelectedTicket({
      ...ticket,
      type,
    });
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setSelectedTicket(null);
    setShowDetailModal(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    dispatch(
      createUserTickets(
        user.token,
        {
          reclamationCode: formData.type,
          company: activeCompany._id,
          description: formData.description,
        },
        "management",
        handleCreateTicketCallback
      )
    );
  };

  const handleCreateTicketCallback = async ({ success, message, data }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });

    if (success) {
      if (attachment) {
        try {
          const formData = new FormData();
          formData.append("documents", attachment);

          await dispatch(
            uploadTicketDocuments(
              user.token,
              data.reference,
              formData,
              () => {}
            )
          );

          setAttachment(null);
        } catch (error) {
          Toast.fire({
            icon: "error",
            title: "Erreur",
            text: "Échec du téléchargement du fichier.",
          });
        }
      }
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      setFormData({ type: "", description: "" });
      handleModalClose();
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }

    setSubmitting(false);
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "initiated":
        return { color: "#FF5500", icon: <FaPauseCircle /> };
      case "in_progress":
        return { color: "#FFC107", icon: <FaHourglassHalf /> };
      case "fulfilled":
        return { color: "#28A745", icon: <FaCheckCircle /> };
      case "failed":
        return { color: "#FF0000", icon: <FaTimesCircle /> };
      default:
        return { color: "#6C757D", icon: <FaPauseCircle /> };
    }
  };

  function getNameByCode(code) {
    const type = ticketTypes.find((item) => item.code === code);
    return type ? type.name : "Code non trouvé";
  }

  // Filtrage des tickets selon la recherche et le statut sélectionné
  const filteredTickets = userTickets.filter((ticket) => {
    const statusMatch = selectedStatus
      ? ticket.status === selectedStatus
      : true;
    const searchMatch = searchTerm
      ? getNameByCode(ticket.reclamationCode)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        ticket.reference.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    return statusMatch && searchMatch;
  });

  return (
    <Container fluid style={{ minHeight: "100vh" }}>
      <Loader isLoading={submitting} />

      <Row className="align-items-center justify-content-between mb-3 g-3">
        <Col xs="auto">
          <h4 className="fw-600 text-dark text-start">
            <FaBriefcase size={24} /> Gestion d'entreprise
          </h4>
          <p style={{ maxWidth: "250px" }} className="text-muted text-start">
            Suivez vos demandes ou créez-en de nouvelles.
          </p>
        </Col>
        <Col xs="auto justify-content-end">
          <Row className="mb-3 d-flex justify-content-end g-3">
            <Col xs="auto">
              <Form.Control
                type="text"
                placeholder="Rechercher un ticket..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "200px" }}
              />
            </Col>
            <Col xs="auto">
              <Form.Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{ width: "200px" }}
              >
                <option value="">Tous les statuts</option>
                <option value="initiated">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="fulfilled">Terminée</option>
                <option value="failed">Échouée</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button
                onClick={handleModalShow}
                style={{
                  backgroundColor: "#051259",
                  borderColor: "#051259",
                  borderRadius: "10px",
                  padding: "5px 10px",
                  fontWeight: "500",
                }}
              >
                <FaPlusCircle className="me-2" /> Créer un ticket
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        {filteredTickets && filteredTickets.length > 0 ? (
          filteredTickets.map((ticket) => {
            const statusInfo = getStatusInfo(ticket.status);
            return (
              <>
                <Col key={ticket.id} md={6} lg={4}>
                  <Card
                    className="shadow-sm"
                    style={{
                      backgroundColor: "#F9F9F9",
                      borderRadius: "15px",
                      padding: "15px",
                      minHeight: "140px",
                      borderLeft: "4px solid #6C757D",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 style={{ fontWeight: "400", color: "#333" }}>
                        {getNameByCode(ticket.reclamationCode)}
                      </h6>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
                      <span
                        className="mb-0"
                        style={{
                          color: "#555",
                          textAlign: "left",
                          fontSize: "14px",
                        }}
                      >
                        Date : {moment(ticket.createdAt).format("DD/MMM/YYYY")}
                      </span>

                      <span
                        className="mb-0"
                        style={{
                          color: "#555",
                          textAlign: "left",
                          fontSize: "14px",
                        }}
                      >
                        #{ticket.reference}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between">
                      <div
                        className="d-flex align-items-center"
                        style={{
                          color: statusInfo.color,
                          fontSize: "1.5rem",
                        }}
                      >
                        {statusInfo.icon}
                        <span
                          style={{
                            marginLeft: "10px",
                            fontSize: "1rem",
                          }}
                        >
                          {ticket.status === "initiated"
                            ? "En attente"
                            : ticket.status === "in_progress"
                            ? "En cours"
                            : ticket.status === "fulfilled"
                            ? "Terminée"
                            : ticket.status === "failed" && "Échouée"}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between">
                        {ticket.status === "fulfilled" && ticket.comment && (
                          <Button
                            size="sm"
                            variant="success"
                            style={{
                              borderRadius: "10px",
                              marginRight: "5px",
                            }}
                            onClick={() => openCommentModal(ticket)}
                          >
                            <FaComment />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-primary"
                          style={{ borderRadius: "10px" }}
                          onClick={() => openDetailModal(ticket)}
                        >
                          <FaEye className="me-1" />
                          Consulter
                        </Button>
                      </div>
                    </div>
                  </Card>
                </Col>
              </>
            );
          })
        ) : (
          <div
            className="mt-5"
            style={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              color: "#6c757d",
            }}
          >
            <FaRegFileAlt size={66} color="#ccc" />
            <p style={{ fontSize: 18, marginTop: "10px" }}>
              Aucun ticket créé.
            </p>
          </div>
        )}
      </Row>

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nouveau Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Types de demande</Form.Label>
              <Form.Control
                as="select"
                name="type"
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                required
              >
                <option value="">Choisir le type</option>
                {ticketTypes
                  .filter((type) => type.service === "management")
                  .map((type) => {
                    return <option value={type.code}>{type.name}</option>;
                  })}
              </Form.Control>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Décrivez votre besoin..."
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pièce jointe (optionnelle)</Form.Label>
              <div className="d-flex flex-column align-items-start">
                <div>
                  <label
                    htmlFor="file-upload"
                    className="btn btn-outline-secondary"
                  >
                    <FaPaperclip /> Ajouter un fichier
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                </div>
                {attachment && (
                  <div className="mt-2">
                    <span>
                      {attachment.name}{" "}
                      <FaRegTimesCircle
                        onClick={() => setAttachment(null)}
                        style={{
                          cursor: "pointer",
                          fontSize: "20px",
                          color: "red",
                        }}
                      />
                    </span>
                  </div>
                )}
              </div>
            </Form.Group>

            <div className="text-end">
              <Button
                variant="secondary"
                onClick={handleModalClose}
                className="me-2"
              >
                Annuler
              </Button>
              <Button variant="primary" type="submit">
                Soumettre
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <TicketDetailModal
        show={showDetailModal}
        onHide={closeDetailModal}
        ticket={selectedTicket}
      />

      <Modal show={showCommentModal} onHide={closeCommentModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Commentaire de l'opérateur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">{selectedTicket?.comment}</p>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default UserPlatformGestTicket;
